import {useEffect, useMemo, useState} from 'react';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getTablesAndTicketsTiersSelector, getPrivateTablesAndTicketsTiersSelector} from '@/store/organization-tables';
//formik
import {useFormikContext} from 'formik';
import {AddTableTicketFormValues} from '../formConfiguration';
//mui
import {Box, Typography} from '@mui/material';
//constants
import {
  AMOUNT,
  CAPTION,
  FIELD,
  FORM,
  H5,
  OUTLINE,
  PRICE,
  SUBTITLE1,
  SUBTOTAL,
  TEXT,
  TICKETS_TIER,
  TOTAL,
  ZERO,
} from '@/utils/constants';
//components
import Button from '@/shared/components/Button';
import FormHelperText from '@/shared/components/FormHelperText';
import AddTicketActions from '../AddTicketActions';
//types
import {IOrganizationTicketsTiers} from '@/utils/types';
//icons
import {ReactComponent as PlusIcon} from '@/assets/icons/plus.svg';
import {ReactComponent as MinusIcon} from '@/assets/icons/minus.svg';
import {ReactComponent as MoneyIcon} from '@icons/sidebar/money.svg';
import {ReactComponent as SuccessIcon} from '@icons/successfully.svg';
//helpers
import {
  getCurrentAmount,
  getNewTableValues,
  getTablesCalculatedTotal,
  getTablesSubtotal,
  getUpdatedTableValues,
} from './helpers';
//styles
import {ticketsInfoStyle} from './FormContent.styles';
import {PAYMENT_METHOD, USER_TYPE} from '@/utils/enums';
import {toast} from 'react-toastify';
import Modal from '@/shared/components/Modal';
import MakeDonationModal, {donationOptions} from '@/components/User/MakeDonationModal';
import {getUserInfo, getUserSelector} from '@/store/user';
import {getDonationListByEvent, userMakeDonation} from '@/store/user-donation/actions';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getErrorMessage} from '@/utils/helpers';
import {userSidebarStyle} from '@/layouts/UserTemplate/UserSidebar/UserSidebar.styles';
import {currencyFormatter} from '@/utils/helpers/currency';
import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {userAPI} from '@/core/api';
import {usePaymentStatusPolling} from '@/utils/hooks';
import Loader from '@/shared/components/Loader';
import PaymentOptions from '@/pages/User/Profile/General/PaymentOptions';
import {useSelector} from 'react-redux';
import {profileContactList} from '@/pages/User/Profile/General/ProfileGeneral.style';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import {OrganizationAttendeesService} from '@/utils/services';
import axios from 'axios';
import {useLocation} from 'react-router-dom';
import {useCardAttachment, WaitResult} from '@/utils/hooks/useCardAttachment';

const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const TicketsInfo = ({isFromPage}: {isFromPage?: boolean}) => {
  const {
    errors,
    touched,
    values: {setTables},
    setFieldValue,
  } = useFormikContext<AddTableTicketFormValues>();

  const {role} = useApiInstance();
  const user = useSelector(getUserSelector);
  const event = useSelector(getCurrentEventSelector);

  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(donationOptions[0]);
  const [error, setError] = useState('');
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  const dispatch = useAppDispatch();
  const location = useLocation();

  const {isStarting, isWaiting, waitForAttachment, startAttachment} = useCardAttachment(event?.id, location.pathname);

  const ordinaryTicketsTiers = useAppSelector(getTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];
  const privateTicketsTiers = useAppSelector(getPrivateTablesAndTicketsTiersSelector) as IOrganizationTicketsTiers[];

  const ticketsTiers = role === USER_TYPE.USER && privateTicketsTiers ? privateTicketsTiers : ordinaryTicketsTiers;

  const {startPollingFromUrl} = usePaymentStatusPolling({
    apiInstance: userAPI,
  });

  const handleAttentionModal = () => setIsAttentionModalOpen(!isAttentionModalOpen);

  // NEW: тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  const handleNextStep = () => setPaymentStep(2);
  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  const addPaymentOption = async () => {
    if (!user?.bidderId || !user?.id) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }

      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMakeDonationModalShow = () => {
    if (!user?.paymentMethod) {
      setIsShowPaymentModal(true);
      return;
    }
    if (!event?.isFundTheMission) {
      handleAttentionModal();
    } else {
      setIsDonationModalOpen(true);
      setSelectedOption(donationOptions[0]);
    }
  };

  const handleAddDonation = async () => {
    if (!user || !event) return;

    const numericAmount = parseFloat(selectedOption.replace(',', '.'));

    if (!selectedOption || Number.isNaN(numericAmount) || numericAmount <= 0) {
      toast.error('Donation amount should be greater than 0');
      return;
    }

    const reqBody = {
      amount: Number(numericAmount),
      eventId: user?.bidderId,
    };

    try {
      await dispatch(userMakeDonation({reqBody, apiInstance: userAPI})).unwrap();
      await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();
      setIsSuccessModalOpen(true);
      setIsDonationModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setError('');
    }
  };

  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!', {toastId: 'card-attached'});
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  const handleAddTable = (tableId: number, amount: number) => () => {
    const currentTier = ticketsTiers.find((t) => t.id === tableId);

    const maxAmount = currentTier?.maxAvailableAmount ?? 0;

    if (getCurrentAmountTables(tableId) >= maxAmount) {
      toast.error('You have reached the maximum amount for this tier');
      return;
    }

    const newTable = getNewTableValues(tableId, amount);
    setFieldValue(FIELD.setTables, [...setTables, newTable]);
  };

  const handleDeleteTable = (tableId: number) => () => {
    const updatedTable = getUpdatedTableValues(setTables, tableId);
    setFieldValue(FIELD.setTables, updatedTable);
  };

  const getCurrentAmountTables = (tableId: number) => getCurrentAmount(setTables, tableId);
  const getCurrentSubtotalTables = (tableId: number) => getTablesSubtotal(setTables, ticketsTiers, tableId);

  const calculateTablesTotal = useMemo(
    () => getTablesCalculatedTotal(setTables, ticketsTiers),
    [setTables, ticketsTiers, getCurrentAmountTables, getCurrentSubtotalTables]
  );

  const errorText = useMemo(() => {
    return (errors.setTables && touched.setTables && (errors.setTables as string)) || undefined;
  }, [errors, touched]);

  useEffect(() => {
    startPollingFromUrl();
  }, []);

  if (!ticketsTiers) return <Loader />;

  return (
    <Box component={FORM} sx={ticketsInfoStyle.wrapper}>
      <Box sx={ticketsInfoStyle.header}>
        <Typography variant={CAPTION}>{TICKETS_TIER}</Typography>
        <Typography variant={CAPTION}>{PRICE}</Typography>
        <Typography variant={CAPTION}>{AMOUNT}</Typography>
        <Typography variant={CAPTION}>{SUBTOTAL}</Typography>
      </Box>

      <Box sx={ticketsInfoStyle.content}>
        {ticketsTiers
          // ?.filter((tier) => (user ? tier.isTicketsVisibleOnAuction : true))
          .map((tier) => (
            <Box key={tier.id} sx={ticketsInfoStyle.contentItem}>
              <Box>
                <Box>{tier.name}</Box>
                {tier?.remainingTicketsThresholdValue &&
                  tier?.displayRemainingTickets &&
                  tier.maxAvailableAmount <= tier?.remainingTicketsThresholdValue && (
                    <Typography
                      sx={{color: 'var(--neutral-400)', fontSize: '12px'}}
                    >{`Only ${tier.maxAvailableAmount} tables left`}</Typography>
                  )}
              </Box>

              <Box>{currencyFormatter.format(tier.price)}</Box>

              <Box>
                <Button
                  variant={TEXT}
                  startIcon={<MinusIcon onClick={handleDeleteTable(tier.id)} />}
                  disableRipple
                  disabled={getCurrentAmountTables(tier.id) === ZERO}
                  sx={{
                    ...ticketsInfoStyle.deleteButton,
                    ...(getCurrentAmountTables(tier.id) === ZERO && ticketsInfoStyle.disabledDeleteButton),
                  }}
                />

                <Box sx={ticketsInfoStyle.amount}>{getCurrentAmountTables(tier.id)}</Box>

                <Button
                  variant={TEXT}
                  endIcon={<PlusIcon onClick={handleAddTable(tier.id, tier.numberOfSeats)} />}
                  disableRipple
                  sx={ticketsInfoStyle.addButton}
                />
              </Box>

              <Box>
                <Typography variant={SUBTITLE1}>
                  {currencyFormatter.format(getCurrentSubtotalTables(tier.id))}
                </Typography>
              </Box>
            </Box>
          ))}
        {errorText && <FormHelperText error title={errorText} sx={ticketsInfoStyle.error} />}

        <Box sx={ticketsInfoStyle.footer}>
          <Typography variant={H5}>{TOTAL}</Typography>
          <Typography variant={H5}>{currencyFormatter.format(calculateTablesTotal)}</Typography>
        </Box>

        {role === USER_TYPE.USER && (
          <Button
            variant={OUTLINE}
            title="I can’t attend, but would like to donate"
            startIcon={
              <Box sx={ticketsInfoStyle.moneyIconWrapper}>
                <MoneyIcon />
              </Box>
            }
            onClick={handleMakeDonationModalShow}
          />
        )}
      </Box>

      <AddTicketActions isFromPage={isFromPage} />

      <Modal
        isShowSaveButton
        mainButtonTitle={`Donate $${selectedOption}`}
        isSaveButtonDisable={!selectedOption}
        isShowCloseButton
        closeButtonTitle="Close"
        onClose={() => {
          setIsDonationModalOpen(false);
          setError('');
        }}
        open={isDonationModalOpen}
        sx={userSidebarStyle.modalBid}
        onSave={handleAddDonation}
      >
        <MakeDonationModal selectedOption={selectedOption} setSelectedOption={setSelectedOption} error={error} />
      </Modal>
      <Modal
        open={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setSelectedOption(donationOptions[0]);
        }}
        title="Thank You for Your Donation"
        description={`Your donation of $${selectedOption} successfully processed.`}
        sx={userSidebarStyle.modalLogin}
        closeButtonTitle="Done"
        mainIcon={<SuccessIcon />}
      />
      <Modal
        open={isAttentionModalOpen}
        onClose={handleAttentionModal}
        title="Thank You for Wanting to Help!"
        description="The organizer has paused donation for this auction."
        sx={userSidebarStyle.modalLogin}
      />
      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          isSaveButtonDisable={isStarting}
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default TicketsInfo;
